// ============================================================================
//  DocumentationDialog — the four notes that support an out-patient claim
// ============================================================================
//
//  Complaint, History, Physical Examination, Other Documentation. The same four
//  the consulting room's out-patient screen keeps, saved to the same place by the
//  same endpoint, so a note typed here and a note typed there are one record.
//
//  ── NOT A SECOND STORE ─────────────────────────────────────────────────────
//
//  load   GET  loadPatientDiagnosisdb  { patientId, dat } -> { complaint, history, exam, other }
//  save   POST saveOrUpdateDiagnosisdb { patientId, dat, complaint, history, exam, other, currentUser }
//
//  Both lifted from OutPatient.js rather than invented. The field names are that
//  endpoint's, not this dialog's — `exam` and `other`, not physicalExamination
//  and otherDocumentation — because renaming them on the way out is how the two
//  screens would quietly stop writing to the same columns.
//
//  ── KEYED ON THE PATIENT AND THE VISIT DATE ────────────────────────────────
//
//  `dat` is not decoration: documentation belongs to a VISIT, not to a patient.
//  The same patient seen in April and again in May has two sets of notes, and the
//  claim in front of the officer is for one of them. So the claim's own visit date
//  is what is sent, which is what makes a note land against the attendance being
//  billed rather than against the patient in general.
//
//  ── AUTOSAVE, DEBOUNCED, AND ONLY AFTER A REAL EDIT ────────────────────────
//
//  One second after typing stops, matching the consulting room. The `edited` flag
//  matters more than it looks: without it the load itself sets state, the effect
//  fires, and the dialog saves what it has just read — which on a slow load means
//  saving empty strings over notes that exist. OutPatient.js carries the same
//  guard with a comment calling it the critical fix; this is not a coincidence,
//  it is the same bug waiting in the same shape.
//
//  ── OUT-PATIENT ONLY, FOR NOW ──────────────────────────────────────────────
//
//  An admission's documentation is ward rounds and discharge notes, which is a
//  different form against different tables. The dialog says so rather than
//  offering these four for a stay they do not describe.
//
//  ── IMPORTED CLAIMS: THE FOLDER NUMBER IS THE KEY ──────────────────────────
//
//  This dialog first refused imported claims, on the grounds that an NHIA folder
//  number (P-0001-26042985) is the scheme's identifier and not this hospital's
//  patient id, so a note filed under it would sit where no consulting-room screen
//  reads. That refusal has been removed deliberately, for three measured reasons.
//
//  1. There is no bridge to refuse in favour of. A sweep of every text column in
//     the database found an imported claim's folder number in exactly one place —
//     the claims table itself. Nothing maps it to a patient id, so "use the real
//     id instead" is not an option that exists; the choice was between the folder
//     number and nothing.
//
//  2. The refusal was arbitrary in practice. It fired on the shape of the string,
//     and the codes are not one shape: alongside P-0001-26042985 the same column
//     holds 26/0253. So some imported claims were refused and others silently
//     accepted, split by punctuation rather than by anything real.
//
//  3. Read and write use the same key, so it is self-consistent. Notes typed
//     against a folder number come back under that folder number, on that claim,
//     for anyone who opens it. That is the whole job here — documenting a claim
//     that arrived by XML, for a visit this hospital may hold no other record of.
//
//  What remains true, and is worth knowing rather than hiding: where the id is a
//  scheme folder number, the note lives with the CLAIM rather than joining the
//  patient's consulting-room record, because there is no patient record to join.
//  Where the id is a hospital patient id — every facility claim, and any imported
//  claim whose code happens to be one — it joins the clinical record exactly as
//  the consulting room's own notes do. One code path, no branch: the endpoint
//  keys on whatever id it is given.
//  ── GENERATE ───────────────────────────────────────────────────────────────
//
//  The button writes the first draft from what is already on the claim — the
//  diagnoses, procedures, investigations and medicines, plus who the patient is,
//  when they came, how long they stayed and how they left. The prompt and the
//  condensing live in backend/claimNoteAi.js; this side only asks and shows.
//
//  Three things it deliberately does NOT do:
//
//    it does not send the claim.  Only { id, source } goes up. The backend
//      re-reads the claim for itself, so the note describes what is recorded
//      rather than whatever this screen happens to be holding.
//
//    it does not overwrite silently.  Anything already typed is a person's work.
//      Generating on top of it asks first.
//
//    it does not decide about Other Documentation.  That is a checkbox, because
//      on many claims there is genuinely nothing more to say and a paragraph
//      invented to fill the box is worse than an empty box.
//  ── TWO TABS ───────────────────────────────────────────────────────────────
//
//  Notes and Lab results are different work with different rules, and putting
//  them on one scroll would have meant a page where autosaved fields and
//  explicitly-saved fields sit side by side looking identical. They are separated
//  because they BEHAVE differently, not to save room:
//
//    Notes        autosave a second after typing stops.
//    Lab results  save when the person says so. A half-typed haemoglobin written
//                 into the laboratory's store is a result somebody will act on.
//
//  The lab tab only appears when the claim actually has investigations on it.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  X, ClipboardList, Check, Loader, Sparkles, AlertTriangle, FlaskConical, Activity,
  //  The Ward stay tab's mark. An admission's documentation is a stay, not a
  //  visit — see InpatientDocumentation.
  Stethoscope,
  //  The patient folder button in the footer.
  Printer,
} from 'lucide-react';
import {
  loadPatientDiagnosisdb, saveOrUpdateDiagnosisdb, claimStoreDocSuggest,
  //  The whole record, assembled in one request for the printable folder.
  claimStoreFolder,
} from '../BaseURL';
import DocumentationLabs from './DocumentationLabs';
import DocumentationVitals from './DocumentationVitals';
//  An admission's documentation is a stay, not a visit. See the component.
import InpatientDocumentation from './InpatientDocumentation';
//  The whole record as one printable document. See its header for why it is
//  print-styled HTML rather than a generated PDF.
import { openPatientFolder } from './patientFolderPrint';
import useDialogGuard from './dialogGuard';
import { BRAND } from '../../brand';

const FIELDS = [
  { key: 'complaint', label: 'Complaint',
    placeholder: "The patient's own complaint — PC: …" },
  { key: 'history', label: 'History',
    placeholder: 'HPC, ODQ, PMHx …' },
  { key: 'exam', label: 'Physical Examination',
    placeholder: 'On examination …' },
  { key: 'other', label: 'Other Documentation',
    placeholder: 'Anything else that supports this claim' },
];

const EMPTY = { complaint: '', history: '', exam: '', other: '' };

export default function DocumentationDialog({
  open, onClose, patientId, patientName, visitDate, attendance, currentUser,
  claimId, claimSource, investigationCount = 0,
  //  Printed at the head of the patient folder. Optional — the document falls
  //  back to whatever the claim itself carries, then to a neutral title, so a
  //  caller that does not know the facility still produces a valid record.
  facilityName,
}) {
  const [tab, setTab] = useState('notes');
  const [notes, setNotes] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  //  Kept apart from `error`, which is about saving. A claim with nothing on it
  //  is not a failure — it is a thing to go and fix — and it reads differently.
  const [genNotice, setGenNotice] = useState(null);
  const [includeOther, setIncludeOther] = useState(false);
  //  Nothing is written until a human has typed. See the header.
  const edited = useRef(false);

  //  The caller passes the editor's own normalised setting, so this is an exact
  //  comparison rather than a substring hedge against 'Out-Patient' / 'outpatient'.
  const isInpatient = attendance === 'inpatient';
  //  The only thing that can stop a save now is having nothing to key it on. An
  //  id the clinical tables do not already know is not a reason to refuse — see
  //  the header — but no id at all leaves the note nowhere to go.
  const blocked = !patientId || !visitDate;

  //  The lab tab needs a claim to read investigations from, and at least one to
  //  read. Deliberately NOT gated on `blocked`: lab results key on the
  //  laboratory's own patient id and specimen number, so a claim whose notes
  //  have nowhere to go may still have results worth entering.
  const hasLabs = !!claimId && investigationCount > 0;

  //  Vitals appear on any OUT-PATIENT claim, whether or not observations were
  //  taken — the point of the tab is that it is where you go to enter them when
  //  they are missing, so hiding it when empty would hide the work.
  //
  //  Admissions are excluded for now. Observations there are taken on every ward
  //  round, so a single set of boxes would misrepresent a fifty-night stay as one
  //  reading. That is a chart, and it is a different screen.
  const hasVitals = !!claimId && attendance !== 'inpatient';

  /*  An admission's own documentation: the ward rounds, the nurse's observations
   *  and the shift notes, day by day. Only for an admission, and only when there
   *  is a claim to read the stay from.
   *
   *  This replaces the banner that sat over the four out-patient boxes saying an
   *  admission's documentation "is not built here yet". It was true and it was
   *  the wrong answer: the evidence that justifies a multi-day stay existed in
   *  four tables the whole time, and the screen meant to show the documentation
   *  showed none of it. */
  const hasStay = !!claimId && isInpatient;

  //  The sidebar is drawn when there is anywhere else to go.
  const hasTabs = hasLabs || hasVitals || hasStay;

  /*  ── THE PATIENT FOLDER ───────────────────────────────────────────────────
   *
   *  Gathered in one request and rendered into its own window. See
   *  patientFolderPrint for why it is a printable document rather than a
   *  generated PDF, and why it is a window rather than a download.
   *
   *  A BLOCKED POPUP IS REPORTED. It is the commonest reason a print button
   *  appears to do nothing, and "nothing happened" is the least useful thing a
   *  screen can say.
   */
  const [folderBusy, setFolderBusy] = useState(false);
  const printFolder = async () => {
    setFolderBusy(true); setError(null);
    try {
      const { data } = await axios.get(claimStoreFolder, {
        params: { id: claimId, source: claimSource },
      });
      if (!data?.success) throw new Error(data?.message || 'The folder could not be gathered.');
      const opened = openPatientFolder(data, { facility: facilityName });
      if (!opened) {
        setError('The folder could not open because this browser blocked the new '
          + 'window. Allow pop-ups for this site and press it again.');
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally { setFolderBusy(false); }
  };

  //  An admission opens on its STAY, not on the out-patient boxes — that is what
  //  the officer came for. The four boxes stay reachable, because a typed note
  //  against the visit is still saved and still counts.
  useEffect(() => {
    if (open && hasStay) setTab('stay');
  }, [open, hasStay]);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    edited.current = false;
    setSavedAt(null); setError(null); setGenNotice(null);
    //  Every claim opens on the notes. Landing on whichever tab the last claim
    //  was left on would hide the four fields this dialog is mainly for.
    //  An ADMISSION is moved straight on to its stay by the effect above.
    setTab('notes');
    if (blocked) { setNotes(EMPTY); return undefined; }
    setLoading(true);
    axios.get(loadPatientDiagnosisdb, { params: { patientId, dat: visitDate } })
      .then(({ data }) => {
        if (!alive) return;
        const d = (data && data.success && data.data) ? data.data : {};
        setNotes({
          complaint: d.complaint || '', history: d.history || '',
          exam: d.exam || '', other: d.other || '',
        });
      })
      .catch((e) => {
        if (!alive) return;
        setNotes(EMPTY);
        setError(e?.response?.data?.message || e.message);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, patientId, visitDate, blocked]);

  //  Debounced write. Depends on `notes` rather than on each field so a single
  //  timer covers all four — typing in one and tabbing to the next is one save,
  //  not two racing each other.
  useEffect(() => {
    if (!open || !edited.current || loading || blocked) return undefined;
    const t = setTimeout(async () => {
      setSaving(true); setError(null);
      try {
        await axios.post(saveOrUpdateDiagnosisdb, {
          patientId, dat: visitDate, ...notes, currentUser: currentUser || null,
        });
        setSavedAt(new Date());
      } catch (e) {
        setError(e?.response?.data?.message || e.message);
      } finally { setSaving(false); }
    }, 1000);
    return () => clearTimeout(t);
  }, [notes, open, loading, blocked, patientId, visitDate, currentUser]);

  const set = useCallback((key, value) => {
    edited.current = true;
    setNotes((n) => ({ ...n, [key]: value }));
  }, []);

  const hasTyped = Object.values(notes).some((v) => v.trim());

  //  ── ASKING BEFORE OVERWRITING ────────────────────────────────────────────
  //  This was window.confirm, which is the wrong tool twice over: it is the
  //  browser's box rather than this application's, and it can only say what it is
  //  told in one blunt line.
  //
  //  What it has to say matters here. Because the dialog autosaves, the text in
  //  the boxes is not a draft — it is ALREADY in the patient's record. So this is
  //  not "you will lose what you typed", it is "you will overwrite what is
  //  filed". Those are different warnings and only one of them is true.
  const [confirmOpen, setConfirmOpen] = useState(false);
  //  Typed notes, and the backdrop used to bin them. Escape still cancels the
  //  inner confirmation below — that one is a question, not written work.
  const { panelRef, backdropProps } = useDialogGuard();

  const runGenerate = useCallback(async () => {
    if (!claimId || generating) return;
    setConfirmOpen(false);
    setGenerating(true); setGenNotice(null); setError(null);
    try {
      const { data } = await axios.post(claimStoreDocSuggest, {
        id: claimId, source: claimSource, includeOther,
      });
      const n = data?.note || {};
      //  Marked as edited so the dialog's own autosave picks the note up — the
      //  generated text is filed exactly like typed text, one second later,
      //  which also means an officer who closes immediately keeps it.
      edited.current = true;
      setNotes((prev) => ({
        complaint: n.complaint || '',
        history: n.history || '',
        exam: n.exam || '',
        //  Unchecked means the model was never asked for it, so the box keeps
        //  whatever was there rather than being cleared by an absent field.
        other: includeOther ? (n.other || '') : prev.other,
      }));
    } catch (e) {
      const r = e?.response;
      //  Each of these has a different fix, so each says a different thing.
      if (r?.status === 422) {
        setGenNotice({
          kind: 'empty',
          text: r.data?.message,
          missing: r.data?.missing || [],
        });
      } else if (r?.status === 403) {
        setGenNotice({ kind: 'licence', text:
          'This facility is not licensed for AI note generation. The four boxes '
          + 'can still be filled in by hand.' });
      } else if (r?.status === 503) {
        setGenNotice({ kind: 'licence', text:
          r.data?.message || 'The AI service is not available right now.' });
      } else {
        setGenNotice({ kind: 'failed', text:
          r?.data?.message || e.message || 'The note could not be generated.' });
      }
    } finally { setGenerating(false); }
  }, [claimId, claimSource, includeOther, generating]);

  //  Empty boxes have nothing to protect, so the first generation is one click.
  //  The confirmation is for REPLACING, which is the only destructive case.
  const requestGenerate = useCallback(() => {
    if (!claimId || generating) return;
    if (hasTyped) { setConfirmOpen(true); return; }
    runGenerate();
  }, [claimId, generating, hasTyped, runGenerate]);

  //  Escape cancels the confirmation. The one thing everybody tries when a box
  //  appears over their work, and the safe answer is the one it gives.
  useEffect(() => {
    if (!confirmOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setConfirmOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirmOpen]);

  if (!open) return null;

  return (
    <div role="dialog" aria-modal="true" aria-label="Documentation"
      {...backdropProps}
      style={{
        position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(15,23,42,.42)',
        //  ── WHY THIS IS NOT alignItems: 'center' ─────────────────────────────
        //  A flex container CANNOT scroll to content it has centred out of view:
        //  once the card is taller than the viewport, centring pushes its top
        //  above the container's own top edge, and overflow cannot reach back
        //  past that edge. The header is simply gone. Switching to flex-start
        //  fixed the clipping but gave up the centring.
        //
        //  `margin: auto` on the CARD does both. With free space it centres
        //  exactly like alignItems would; without it, the auto margins collapse
        //  to zero and the card overflows normally — so the backdrop's scroll can
        //  still reach the top of it.
        display: 'flex', padding: '24px 16px', overflow: 'auto',
      }}>
      <div ref={panelRef}
        style={{
          //  Sized to its CONTENT, with a ceiling — not stretched to the viewport.
          //  Filling the screen gave the notes tab a field of empty white below
          //  four short boxes, and made a dialog that is mostly used for three
          //  sentences feel like a page.
          //
          //  The lab form is what needs the width: a full blood count is 22
          //  parameters, and they want to be checkable at a glance before saving.
          //  1040 fits four of them per row beside the sidebar without the dialog
          //  taking over the screen.
          width: '100%', maxWidth: 1040, background: '#fff', borderRadius: 12,
          border: `1px solid ${BRAND.line}`, boxShadow: '0 24px 60px rgba(15,23,42,.28)',
          //  Centres it when there is room, and does NOT clip the top when there
          //  is not. See the backdrop above — this is the half that does the
          //  centring, and it is the only half that can do it safely.
          margin: 'auto',
          //  Matches the backdrop's 24px padding on both edges, so the card is
          //  never taller than the space it is being placed in.
          maxHeight: 'calc(100vh - 48px)',
          display: 'flex', flexDirection: 'column',
          //  The regenerate confirmation is positioned against this card, so it
          //  covers the note it is asking about and nothing else on the screen.
          position: 'relative',
        }}>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
          background: 'linear-gradient(135deg,#123A6D,#0B2452)', color: '#fff',
          borderRadius: '12px 12px 0 0',
        }}>
          <div style={{
            width: 34, height: 34, borderRadius: 9, flexShrink: 0,
            display: 'grid', placeItems: 'center', background: 'rgba(255,255,255,.14)',
          }}><ClipboardList size={16} /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, lineHeight: 1.25 }}>Documentation</div>
            <div style={{ fontSize: 11.5, opacity: .82, overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {patientName ? `${patientName} · ` : ''}{patientId || 'no folder number'}
              {visitDate ? ` · ${String(visitDate).slice(0, 10)}` : ''}
            </div>
          </div>

          {/*  Saved / saving, in the header where it is visible from any field.
               An autosaving form with no acknowledgement is one people retype
               into, because nothing ever told them it took. */}
          <span style={{ fontSize: 11.5, opacity: .9, display: 'inline-flex',
            alignItems: 'center', gap: 5, marginRight: 4, whiteSpace: 'nowrap' }}>
            {saving ? (<><Loader size={12} /> Saving…</>)
              : savedAt ? (<><Check size={12} /> Saved{' '}
                {savedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</>)
                : null}
          </span>

          <button type="button" onClick={onClose} aria-label="Close"
            style={{
              border: 0, background: 'rgba(255,255,255,.14)', cursor: 'pointer',
              width: 28, height: 28, borderRadius: 7, color: '#fff',
              display: 'grid', placeItems: 'center', flexShrink: 0,
            }}><X size={14} /></button>
        </div>

        {/*  ── SIDEBAR AND PANES ────────────────────────────────────────────
             The navigation runs down the left rather than across the top. Two
             reasons, and the second is the one that decided it: a vertical list
             has room to grow — Imaging and whatever follows it are more sections,
             not a row that starts wrapping — and it puts the section labels in
             the same column as the field labels below them, so the eye reads down
             one edge instead of tracking a horizontal strip and then jumping.

             minHeight:0 on the row is load-bearing. Without it a flex child
             refuses to shrink below its content, the row grows past the card, and
             the inner scroll never engages — the whole dialog scrolls instead and
             the header leaves the screen. */}
        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          {hasTabs && (
            <nav style={{
              width: 168, flexShrink: 0, padding: '10px 8px',
              borderRight: `1px solid ${BRAND.line}`, background: '#F8FAFC',
              display: 'flex', flexDirection: 'column', gap: 2,
              borderRadius: '0 0 0 12px',
            }}>
              {[
                //  First, because on an admission it IS the documentation. The
                //  four boxes below describe a single out-patient encounter and
                //  cannot describe a stay.
                { key: 'stay', label: 'Ward stay', icon: Stethoscope,
                  hint: 'Rounds, observations, notes', show: hasStay },
                { key: 'notes', label: 'Notes', icon: ClipboardList,
                  hint: 'Complaint, history, examination', show: true },
                { key: 'labs', label: 'Lab results', icon: FlaskConical,
                  hint: `${investigationCount} investigation${investigationCount === 1 ? '' : 's'}`,
                  show: hasLabs },
                { key: 'vitals', label: 'Vitals', icon: Activity,
                  hint: 'Temperature, pulse, pressure', show: hasVitals },
              ].filter((t) => t.show).map((t) => {
                const active = tab === t.key;
                const Icon = t.icon;
                return (
                  <button key={t.key} type="button" onClick={() => setTab(t.key)}
                    aria-current={active ? 'page' : undefined}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%',
                      padding: '8px 9px', border: 0, font: 'inherit', textAlign: 'left',
                      fontSize: 12.5, cursor: 'pointer', borderRadius: 7,
                      background: active ? '#fff' : 'transparent',
                      boxShadow: active ? '0 1px 2px rgba(15,23,42,.08)' : 'none',
                      color: active ? BRAND.ink : BRAND.inkMuted,
                      //  The active marker is on the left edge, where the eye
                      //  already is on a vertical list.
                      borderLeft: `3px solid ${active ? '#123A6D' : 'transparent'}`,
                    }}>
                    <Icon size={14} style={{ marginTop: 1, flexShrink: 0 }} />
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', fontWeight: active ? 800 : 600 }}>
                        {t.label}
                      </span>
                      <span style={{ display: 'block', fontSize: 10.5, marginTop: 1,
                        color: BRAND.inkMuted, lineHeight: 1.3 }}>{t.hint}</span>
                    </span>
                  </button>
                );
              })}
            </nav>
          )}

          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            {/*  Mounted only when it is the open tab, unlike the panes below.
                 The stay is several queries across four tables and nobody wants
                 it read for an out-patient claim that will never show it — and
                 it holds no unsaved input, so nothing is lost by unmounting. */}
            {hasStay && tab === 'stay' && (
              <div style={{ padding: 14, overflow: 'auto', flex: 1, minHeight: 0 }}>
                {/*  currentUser signs whatever is added — a ward round, an
                     observation, a nursing note. patientName goes onto the
                     vitals and note rows, which carry it alongside the id the
                     way the ward screens write them. */}
                <InpatientDocumentation
                  claimId={claimId} claimSource={claimSource} aiEnabled
                  currentUser={currentUser} patientName={patientName} />
              </div>
            )}

            <div style={{ padding: 14, overflow: 'auto', flex: 1,
              display: tab === 'labs' ? 'block' : 'none' }}>
              <DocumentationLabs
                claimId={claimId} claimSource={claimSource} currentUser={currentUser} />
            </div>

            <div style={{ padding: 14, overflow: 'auto', flex: 1,
              display: tab === 'vitals' ? 'block' : 'none' }}>
              <DocumentationVitals
                claimId={claimId} claimSource={claimSource} currentUser={currentUser} />
            </div>

            {/*  display:none rather than unmounting. Unmounting would throw away a
                 half-typed note and cancel its pending autosave the moment
                 somebody glanced at another tab. */}
            <div style={{ padding: 14, overflow: 'auto', flex: 1,
              display: tab === 'notes' ? 'block' : 'none' }}>
          {isInpatient && (
            <div style={{
              marginBottom: 12, padding: '9px 12px', borderRadius: 8,
              background: '#FFF4E5', border: '1px solid rgba(154,91,0,.28)',
              color: '#7A4A00', fontSize: 12, lineHeight: 1.5,
            }}>
              {/*  REWRITTEN: this used to end "it is not built here yet", which
                   was true and was the wrong answer — the evidence that justifies
                   a multi-day stay was sitting in four ward tables the whole
                   time. It now points at the tab that shows it. A note that
                   describes a gap the screen no longer has is worse than none,
                   because the officer believes it and stops looking. */}
              <strong>This is an admission.</strong> These four notes are the
              out-patient form — one visit, one set of notes. The stay's own
              documentation is the ward rounds, the observations and the nursing
              notes, day by day, and it is on the <strong>Ward stay</strong> tab.
              Anything typed below is still saved against this visit.
            </div>
          )}

          {/*  The one remaining reason the fields can be dead. Named precisely —
               a claim missing its date and a claim missing its folder number need
               different things fixed. */}
          {blocked && (
            <div style={{
              marginBottom: 12, padding: '10px 12px', borderRadius: 8,
              background: '#FFF4E5', border: '1px solid rgba(154,91,0,.28)',
              color: '#7A4A00', fontSize: 12, lineHeight: 1.55,
            }}>
              <strong style={{ color: '#5E3A00' }}>Nothing to file this against.</strong>
              <div style={{ marginTop: 5 }}>
                This claim has no {patientId ? 'visit date' : 'folder number'}, so
                documentation has no patient and date to belong to. Fill it in on the
                claim first and this will open.
              </div>
            </div>
          )}

          {error && (
            <div style={{
              marginBottom: 12, padding: '9px 12px', borderRadius: 8,
              background: BRAND.dangerSoft, border: '1px solid rgba(166,40,40,.22)',
              color: '#A62828', fontSize: 12,
            }}>
              {error}
            </div>
          )}

          {/*  Why the generator did not write anything. The empty-claim case is
               the one that matters: it is not an error, it is a claim that has
               not had its treatment entered yet, and naming exactly which four
               things are missing turns a dead end into a task. */}
          {genNotice && (
            <div style={{
              marginBottom: 12, padding: '10px 12px', borderRadius: 8,
              display: 'flex', gap: 9, alignItems: 'flex-start',
              background: '#FFF4E5', border: '1px solid rgba(154,91,0,.28)',
              color: '#7A4A00', fontSize: 12, lineHeight: 1.55,
            }}>
              <AlertTriangle size={14} style={{ marginTop: 1, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ color: '#5E3A00' }}>
                  {genNotice.kind === 'empty' ? 'Nothing on this claim to write from.'
                    : genNotice.kind === 'licence' ? 'Generating is not available.'
                      : 'The note could not be generated.'}
                </strong>
                <div style={{ marginTop: 4 }}>{genNotice.text}</div>
                {genNotice.kind === 'empty' && genNotice.missing?.length > 0 && (
                  <div style={{ marginTop: 6 }}>
                    Missing: {genNotice.missing.join(', ')}. Add these on the claim,
                    then generate.
                  </div>
                )}
              </div>
              <button type="button" onClick={() => setGenNotice(null)} aria-label="Dismiss"
                style={{
                  border: 0, background: 'none', cursor: 'pointer', padding: 0,
                  color: '#7A4A00', flexShrink: 0, lineHeight: 1,
                }}><X size={13} /></button>
            </div>
          )}

          {/*  Two by two. Four stacked full-width boxes pushed Other Documentation
               below the fold on a laptop, and these are read together — a
               complaint is judged against the examination beside it.

               340, not 300. At 300 the grid fitted THREE columns whenever the
               sidebar was absent, so the same four notes were laid out
               differently depending on whether the claim happened to have
               investigations on it. 340 gives two columns either way, and still
               drops to one on a narrow screen. */}
          <div style={{
            display: 'grid', gap: 12,
            gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
          }}>
            {FIELDS.map((f) => (
              <label key={f.key} style={{ display: 'block', minWidth: 0 }}>
                <span style={{
                  display: 'block', fontSize: 11, fontWeight: 800, letterSpacing: .4,
                  textTransform: 'uppercase', color: BRAND.inkMuted, marginBottom: 5,
                }}>{f.label}</span>
                {/*  Locked while generating too — typing into a box that is about
                     to be replaced loses the typing without saying so. */}
                <textarea
                  value={notes[f.key]}
                  onChange={(e) => set(f.key, e.target.value)}
                  disabled={blocked || loading || generating}
                  placeholder={loading ? 'Loading…'
                    : generating ? 'Reading the claim…' : f.placeholder}
                  rows={7}
                  style={{
                    width: '100%', boxSizing: 'border-box', resize: 'vertical',
                    padding: '9px 11px', fontSize: 12.5, lineHeight: 1.55,
                    font: 'inherit', fontFamily: 'inherit', color: BRAND.ink,
                    border: `1px solid ${BRAND.line}`, borderRadius: 8,
                    background: blocked || loading || generating ? '#F8FAFC' : '#fff',
                    outline: 'none',
                  }} />
              </label>
            ))}
            </div>
          </div>
          </div>
        </div>

        <div style={{
          padding: '10px 16px', borderTop: `1px solid ${BRAND.line}`,
          background: '#F8FAFC', borderRadius: '0 0 12px 12px',
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}>
          {/*  The generate controls sit together on the left, because the choice
               about Other Documentation only means anything next to the button
               it changes. Wraps rather than squeezing on a narrow screen.

               Shown only on the notes tab: it writes the four NOTES, and a
               Generate button sitting under a blood count form or a set of
               observations would plainly read as generating those instead. Both
               of the other tabs carry their own. */}
          {claimId && !blocked && tab === 'notes' && (
            <>
              <button type="button" onClick={requestGenerate} disabled={generating || loading}
                title="Write a first draft from the diagnoses, procedures, tests and medicines on this claim"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '7px 13px', borderRadius: 7, border: 0, font: 'inherit',
                  fontSize: 12.5, fontWeight: 700, color: '#fff',
                  cursor: generating || loading ? 'default' : 'pointer',
                  opacity: generating || loading ? .6 : 1,
                  background: 'linear-gradient(135deg,#123A6D,#0B2452)',
                }}>
                <Sparkles size={13} />
                {generating ? 'Writing…' : hasTyped ? 'Regenerate' : 'Generate'}
              </button>

              {/*  The user's call, per claim, not a setting buried elsewhere:
                   on plenty of claims there is nothing further to say, and a
                   paragraph invented to fill the box is worse than an empty box. */}
              <label style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: 11.5, color: BRAND.inkMuted, cursor: 'pointer',
                userSelect: 'none',
              }}>
                <input type="checkbox" checked={includeOther}
                  onChange={(e) => setIncludeOther(e.target.checked)}
                  style={{ cursor: 'pointer', margin: 0 }} />
                Include other documentation
              </label>
            </>
          )}

          {/*  Names the key it writes under rather than saying "this patient".
               On an imported claim that key is the folder number, and an officer
               who can see which id the note is filed under can tell whether two
               claims for the same person will share it. */}
          {/*  The footer text follows the tab, because the two tabs save by
               different rules and a line promising autosave under a form that
               does not autosave would be a lie in the place it matters most. */}
          <span style={{ fontSize: 11.5, color: BRAND.inkMuted, flex: 1, minWidth: 200 }}>
            {/*  ── THE STAY BRANCH IS NOT IN THE TREE THIS CAME FROM ──────────
                 There it fell through to the autosave line, which on a read-only
                 timeline is exactly the lie the comment above forbids: nothing on
                 the Ward stay tab saves as you type, and the entry forms it
                 offers save only when pressed. Added rather than copied. */}
            {tab === 'stay'
              ? 'The ward record is shown as it was written — the round belongs to the '
                + 'doctor who did it. Anything added here is saved when you press Save '
                + 'on its own form.'
              : tab === 'labs'
              ? 'Results are saved to the laboratory record when you press Save — '
                + 'they are not saved as you type.'
              : tab === 'vitals'
                ? 'Observations are saved to the patient\'s chart when you press Save — '
                  + 'they are not saved as you type.'
                : (<>Saves on its own a second after you stop typing
                  {blocked ? '.' : <> — filed under <strong style={{ color: BRAND.inkSoft }}>
                    {patientId}</strong> for {String(visitDate).slice(0, 10)}.</>}</>)}
          </span>

          {/*  ── THE WHOLE RECORD, AS A DOCUMENT ────────────────────────────
               ASKED FOR: "a report we can populate — a Word document or a PDF we
                can print out and give to them, with all the information of the
                documentation and everything, lab results and everything,
                arranged nicely as a professional hospital system."

               In the FOOTER rather than as a fifth tab, because it is not
               another view of part of the record — it is the whole of it, and it
               reads the same whichever tab you happen to be on.

               Assembled by ONE request, so the document cannot be missing a
               section without saying so. See claimStoreFolder and
               patientFolderPrint. */}
          {claimId && (
            <button type="button" onClick={printFolder} disabled={folderBusy}
              title={'The claim, the notes, the observations, the stay and the '
                + 'laboratory results as one printable record'}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '7px 13px', borderRadius: 7,
                cursor: folderBusy ? 'default' : 'pointer',
                fontSize: 12.5, fontWeight: 800, font: 'inherit',
                border: `1px solid ${BRAND.navy}33`, background: BRAND.navySoft,
                color: BRAND.navy,
              }}>
              {folderBusy ? <Loader size={13} /> : <Printer size={13} />}
              {folderBusy ? 'Gathering…' : 'Patient folder'}
            </button>
          )}

          <button type="button" onClick={onClose}
            style={{
              padding: '7px 14px', borderRadius: 7, cursor: 'pointer',
              fontSize: 12.5, fontWeight: 700, font: 'inherit',
              border: `1px solid ${BRAND.line}`, background: '#fff', color: BRAND.inkSoft,
            }}>Close</button>
        </div>

        {/*  ── REGENERATE CONFIRMATION ──────────────────────────────────────
             Sits inside the dialog and covers it, rather than being a second
             floating window: it is a question about THIS note, and it should
             read as part of it.

             The wording is specific about the real consequence. Because the
             dialog autosaves, what is in the boxes is already filed against the
             patient — so this replaces a record, it does not discard a draft.
             The buttons say what they do; neither is "OK", which tells nobody
             anything at the moment they most need telling. */}
        {confirmOpen && (
          <div
            role="alertdialog" aria-modal="true" aria-label="Regenerate this note"
            onClick={() => setConfirmOpen(false)}
            style={{
              position: 'absolute', inset: 0, zIndex: 2, borderRadius: 12,
              background: 'rgba(15,23,42,.55)', display: 'flex',
              alignItems: 'center', justifyContent: 'center', padding: 20,
            }}>
            <div onClick={(e) => e.stopPropagation()}
              style={{
                width: '100%', maxWidth: 430, background: '#fff', borderRadius: 11,
                border: `1px solid ${BRAND.line}`, padding: '16px 18px 14px',
                boxShadow: '0 18px 44px rgba(15,23,42,.3)',
              }}>
              <div style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
                <div style={{
                  width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                  display: 'grid', placeItems: 'center',
                  background: '#FFF4E5', color: '#9A5B00',
                }}><AlertTriangle size={15} /></div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: BRAND.ink }}>
                    Write a new note over this one?
                  </div>
                  <div style={{
                    marginTop: 6, fontSize: 12.5, lineHeight: 1.6, color: BRAND.inkSoft,
                  }}>
                    The complaint, history and examination here will be replaced by a
                    fresh note written from this claim.
                    <div style={{ marginTop: 7 }}>
                      What is written now is <strong style={{ color: BRAND.ink }}>already
                      saved</strong> to this patient's record for {String(visitDate).slice(0, 10)},
                      so it will be overwritten rather than merely cleared. There is no undo.
                    </div>
                  </div>
                </div>
              </div>

              <div style={{
                display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14,
              }}>
                <button type="button" onClick={() => setConfirmOpen(false)} autoFocus
                  style={{
                    padding: '7px 14px', borderRadius: 7, cursor: 'pointer',
                    fontSize: 12.5, fontWeight: 700, font: 'inherit',
                    border: `1px solid ${BRAND.line}`, background: '#fff',
                    color: BRAND.inkSoft,
                  }}>Keep what I have</button>
                <button type="button" onClick={runGenerate}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '7px 14px', borderRadius: 7, cursor: 'pointer',
                    fontSize: 12.5, fontWeight: 700, font: 'inherit', border: 0,
                    color: '#fff', background: 'linear-gradient(135deg,#123A6D,#0B2452)',
                  }}><Sparkles size={13} /> Write a new note</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
